import { useMsal } from "@azure/msal-react";
import { PageHeader } from "@mj/shared-ui";
import { Form, Input, Space } from "antd";
import { useState, useMemo } from "react";
import { toast } from "react-toastify";

import FailedGroupsBanner from "./components/FailedGroupsBanner";
import MemberFormModal from "./components/MemberFormModal";
import MembersTable from "./components/MembersTable";
import SyncButton from "./components/SyncButton";
import TeamFilterTabs, { TeamFilter } from "./components/TeamFilterTabs";
import {
  useGetAvailableRoles,
  useGetProjectTeamMembers,
  useCreateProjectTeamMember,
  useUpdateProjectTeamMember,
  useDeleteProjectTeamMember,
  useSyncProjectTeamUsers
} from "./hooks/useProjectTeamApi";
import {
  IProjectTeamMember,
  IProjectTeamMemberPayload,
  IUpdateProjectTeamMemberPayload
} from "./types";
import { matchesTeamMemberSearch } from "./utils";

import Layout from "@/components/common/Layout";
import { UK_REGIONS_TO_CODE_MAP } from "@/constants/region";
import { SYSTEM_USERS } from "@/constants/users";
import { useProjects } from "@/hooks/businessCentral";

const ProjectTeamPage = () => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || "";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<IProjectTeamMember | null>(
    null
  );
  const [searchName, setSearchName] = useState("");
  const [activeFilter, setActiveFilter] = useState<TeamFilter>("all");
  const [failedGroups, setFailedGroups] = useState<string[]>([]);
  const [form] = Form.useForm();

  const { data: availableRoles } = useGetAvailableRoles();
  const { data: teamMembers = [], isLoading } = useGetProjectTeamMembers();

  const createMutation = useCreateProjectTeamMember();
  const updateMutation = useUpdateProjectTeamMember();
  const deleteMutation = useDeleteProjectTeamMember();
  const syncMutation = useSyncProjectTeamUsers();

  // Users freshly pulled in by the Azure AD sync (backend sets `IsNewFromSync`).
  const newMembersCount = useMemo(
    () => teamMembers.filter((m) => m.IsNewFromSync).length,
    [teamMembers]
  );

  const filteredTeamMembers = useMemo(() => {
    let list = teamMembers;
    if (activeFilter === "new") {
      list = list.filter((m) => m.IsNewFromSync);
    }
    if (searchName.trim()) {
      list = list.filter((m) => matchesTeamMemberSearch(m, searchName));
    }
    return list;
  }, [teamMembers, searchName, activeFilter]);

  const pluralUsers = (n: number) => `${n} ${n === 1 ? "user" : "users"}`;
  const allUsersLabel = `All ${pluralUsers(teamMembers.length)}`;
  const newUsersLabel =
    newMembersCount === 0
      ? "No new users"
      : `${newMembersCount} new ${newMembersCount === 1 ? "user" : "users"}`;

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync({
        syncedBy: currentUserEmail || undefined
      });
      const added = result.Added.length;

      toast.success(
        added > 0
          ? `Sync complete — ${added} new user${added === 1 ? "" : "s"} added. Assign them a role below.`
          : "Sync complete — no new users found."
      );

      // Surface a persistent banner (not just a toast) so a failed group isn't
      // missed — it stays until the admin dismisses it or syncs again.
      setFailedGroups(result.FailedGroups);

      // Surface the freshly-synced users so the admin can assign roles right away.
      if (added > 0) {
        setActiveFilter("new");
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const selectedRole = Form.useWatch("role", form);
  const selectedRegion = Form.useWatch("region", form);
  const selectedRegionCode = selectedRegion
    ? UK_REGIONS_TO_CODE_MAP[selectedRegion]
    : undefined;

  // Fetch the projects of the region picked in the form, not the signed-in
  // admin's own region (the hook's default), so the Project dropdown follows
  // the Region dropdown.
  const { data: projectsData, isLoading: projectsLoading } =
    useProjects(selectedRegionCode);

  const siteManagerUsersMap = useMemo(() => {
    const mappings: Record<string, string> = {};
    teamMembers.forEach((tm) => {
      if (tm.RoleDisplayName === "Site Manager/Supervisor") {
        mappings[tm.Email] = tm.Project;
      }
    });

    return mappings;
  }, [teamMembers]);

  const availableUsers = useMemo(() => {
    const isSiteManagerRole = selectedRole === "Site Manager/Supervisor";

    return SYSTEM_USERS.filter((user) => {
      if (editingMember && user.email === editingMember.Email) {
        return true;
      }
      if (isSiteManagerRole) {
        return !siteManagerUsersMap[user.email];
      }
      return true;
    });
  }, [selectedRole, siteManagerUsersMap, editingMember]);

  const availableProjects = useMemo(() => {
    if (!selectedRegionCode || !projectsData?.value) return [];

    return projectsData.value
      .filter((p) => p.jobPostingGroup === selectedRegionCode)
      .map((p) => ({
        value: p.description,
        label: p.description
      }));
  }, [selectedRegionCode, projectsData]);

  const handleOpenModal = (member?: IProjectTeamMember) => {
    if (member) {
      setEditingMember(member);
      form.setFieldsValue({
        email: member.Email,
        project: member.Project,
        role: member.RoleDisplayName,
        region: member.Region
      });
    } else {
      setEditingMember(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!editingMember || editingMember.RoleDisplayName !== values.role) {
        const found = teamMembers?.find(
          (item) =>
            item.Email === values.email && item.RoleDisplayName === values.role
        );

        if (found) {
          return toast.error(
            `${values.email} already has a role of "${values.role}"`
          );
        }
      }

      const selectedUser = SYSTEM_USERS.find((u) => u.email === values.email);
      const selectedProjectBc = projectsData?.value?.find(
        (p) => p.description === values.project
      );

      if (editingMember) {
        // The form has no editable "full name" field — it's read-only, sourced
        // from SYSTEM_USERS. Fall back to the member's existing FullName when
        // the email isn't in that hardcoded list (e.g. Azure AD auto-assigned
        // users), and omit the field entirely rather than send an empty
        // string that would wipe out the real value on the backend.
        const fullName = selectedUser?.fullName || editingMember.FullName;
        const updatePayload: IUpdateProjectTeamMemberPayload = {
          role: values.role,
          ...(fullName ? { fullName } : {}),
          department: "Construction",
          region: values.region,
          project: values.project || " ",
          opportunityNo: selectedProjectBc?.opportunityNo ?? undefined
        };
        await updateMutation.mutateAsync({
          email: editingMember.Email,
          payload: updatePayload
        });
        toast.success("Team member updated successfully");
      } else {
        const payload: IProjectTeamMemberPayload = {
          email: values.email,
          project: values.project || " ",
          opportunityNo: selectedProjectBc?.opportunityNo ?? undefined,
          fullName: selectedUser?.fullName || "",
          assignedBy: currentUserEmail,
          role: values.role,
          department: "Construction",
          region: values.region
        };
        await createMutation.mutateAsync(payload);
        toast.success("Team member added successfully");
      }

      handleCloseModal();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleDelete = async (member: IProjectTeamMember) => {
    try {
      await deleteMutation.mutateAsync(member.Email);
      toast.success("Team member removed successfully");
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <Layout>
      <div className="p-2">
        <PageHeader
          title="Project Team"
          description="Manage user access and roles across projects"
          extra={
            <Space size="middle" wrap>
              <SyncButton
                loading={syncMutation.isPending}
                onSync={handleSync}
              />
              <Input
                size="large"
                placeholder="Search by name, email, or role"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                allowClear
                style={{ width: 320 }}
              />
            </Space>
          }
        />

        <FailedGroupsBanner
          failedGroups={failedGroups}
          onClose={() => setFailedGroups([])}
        />

        <TeamFilterTabs
          activeFilter={activeFilter}
          onChange={setActiveFilter}
          allUsersLabel={allUsersLabel}
          newUsersLabel={newUsersLabel}
          newMembersCount={newMembersCount}
        />

        <MembersTable
          members={filteredTeamMembers}
          loading={isLoading || syncMutation.isPending}
          onEdit={handleOpenModal}
          onDelete={handleDelete}
        />

        <MemberFormModal
          isOpen={isModalOpen}
          editingMember={editingMember}
          form={form}
          confirmLoading={createMutation.isPending || updateMutation.isPending}
          availableRoles={availableRoles}
          availableUsers={availableUsers}
          availableProjects={availableProjects}
          projectsLoading={projectsLoading}
          onOk={handleSubmit}
          onCancel={handleCloseModal}
        />
      </div>
    </Layout>
  );
};

export default ProjectTeamPage;
